import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import React, {useState} from 'react';
import type {
  ClientRequest,
  ClientResponse,
  ContactRequest,
} from 'src/api/ProactApiClient';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {BackBar} from 'src/app/layout/BackBar';
import {toFormDataV2} from 'src/app/forms/engine/formData';
import {ProactForm, ProactFormGrid} from 'src/app/forms/engine/ProactForm';
import {useEntitySyncedForm} from 'src/app/forms/engine/useEntitySyncedForm';
import {SidebarLinkClients} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {
  useCreateClientMutation,
  useGetClientQuery,
  useUpdateClientMutation,
} from 'src/redux/queries/client';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {
  ClientDocumentTitle,
  ClientFormMeta,
  MapClientResponseToClientRequest,
} from './clientHelpers';
import {ContactsEditor} from './ContactsFields';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface ClientFormProps {
  id?: number;
  mode: 'create' | 'edit';
}

export const ClientForm: React.FC<ClientFormProps> = ({id, mode}) => {
  const {setAlert} = useAlert();
  const navigate = useAppNavigate();
  const [isSavePending, setIsSavePending] = useState(false);

  const isCreateMode = mode === 'create';
  const isEditMode = mode === 'edit';

  const {data: fetchedClient, isLoading: isClientLoading} = useGetClientQuery(
    id!,
    {skip: !isEditMode || id === undefined}
  );

  const [createClient] = useCreateClientMutation();
  const [updateClient] = useUpdateClientMutation();

  const item: ClientResponse | undefined = fetchedClient;
  const isLoading = isEditMode && isClientLoading;

  const form = useEntitySyncedForm({
    formMeta: ClientFormMeta,
    entity: fetchedClient,
    toFormData: toFormDataV2,
  });

  const handleSave = async () => {
    setIsSavePending(true);

    try {
      const values = form.getFieldValues();
      const contacts = (form.methods.getValues('contacts') ??
        []) as ContactRequest[];
      const model: ClientRequest = MapClientResponseToClientRequest({
        ...values,
        contacts,
        id: item?.id,
      } as ClientResponse);

      let response: ClientResponse;

      if (isCreateMode) {
        response = await createClient(model).unwrap();
        setAlert(`${response.name} was successfully created`, 'success');
      } else {
        response = await updateClient(model).unwrap();
        setAlert(
          `${item?.name ?? 'Client'} was successfully updated`,
          'success'
        );
      }

      navigate(PathUtility.ClientsView(response.id), {replace: true});
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }

    setIsSavePending(false);
  };

  const handleValidateAndSave = async () => {
    const isValid = await form.validateForm();
    if (isValid) {
      void handleSave();
    }
  };

  const {goBack: handleCancel} = useBackNavigation(
    isEditMode && id ? PathUtility.ClientsView(id) : PathUtility.Clients('list')
  );

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  const pageTitle = isCreateMode ? 'New Client' : item?.name ?? 'Edit Client';

  return (
    <Stack>
      <BackBar
        fallbackTo={
          isEditMode && id
            ? PathUtility.ClientsView(id)
            : PathUtility.Clients('list')
        }
        fallbackLabel={isEditMode ? 'Back to Client' : 'Back to Clients'}
        breadcrumbs={[
          {label: ClientDocumentTitle, to: PathUtility.Clients('list')},
          ...(isEditMode && item
            ? [{label: item.name ?? 'Client', to: PathUtility.ClientsView(id!)}]
            : []),
          {label: isCreateMode ? 'New Client' : 'Edit'},
        ]}
      />

      <PageHeader>
        <PageHeader.Title icon={SidebarLinkClients.icon}>
          {pageTitle}
        </PageHeader.Title>

        {isEditMode && (
          <PageHeader.Badges>
            <PageHeader.Badge color="yellow" variant="light">
              Editing
            </PageHeader.Badge>
          </PageHeader.Badges>
        )}

        <PageHeader.Actions>
          <PageHeader.Action variant="cancel" onClick={handleCancel}>
            Cancel
          </PageHeader.Action>
          <PageHeader.Action
            variant={isCreateMode ? 'create' : 'save'}
            onClick={handleValidateAndSave}
            loading={isSavePending}>
            {isCreateMode ? 'Create Client' : 'Save Changes'}
          </PageHeader.Action>
        </PageHeader.Actions>
      </PageHeader>

      <ProactForm form={form}>
        <ProactFormGrid />
        <ContactsEditor />
      </ProactForm>

      <Group>
        <PageHeader.Action variant="cancel" onClick={handleCancel}>
          Cancel
        </PageHeader.Action>
        <PageHeader.Action
          variant={isCreateMode ? 'create' : 'save'}
          onClick={handleValidateAndSave}
          loading={isSavePending}>
          {isCreateMode ? 'Create Client' : 'Save Changes'}
        </PageHeader.Action>
      </Group>
    </Stack>
  );
};
